import { Component, HostListener, OnDestroy, OnInit, inject, DestroyRef, ChangeDetectionStrategy } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NavigationEnd, Router } from '@angular/router';
import { EMPTY, filter, switchMap } from 'rxjs';
import { MatDialog } from '@angular/material/dialog';
import { AppService } from './app.service';
import { GlobalService } from './global/services/global.service';
import { WorkspaceService } from './workspace/workspace.service';
import { IdleService } from './core/services/idle.service';
import { ClientConfirmActionComponent } from './global/components/client-confirm-action/client-confirm-action.component';
import { ClientLoadFailedComponent } from './global/components/client-load-failed/client-load-failed.component';
import { environment } from '../environments/environment';
import { FormViewDrilldownRoutingService } from './admin/components/form-view/form-view-drilldown-routing.service';

@Component({
    selector: 'gtw-root',
    templateUrl: './app.component.html',
    styleUrls: ['./app.component.scss'],
    standalone: false,
  changeDetection: ChangeDetectionStrategy.Eager,
})
export class AppComponent implements OnInit, OnDestroy {
  private readonly destroyRef = inject(DestroyRef);

  baseURLs: string;
  channelKey: string = '';
  headerData: string = '';
  currentTheme: string = '';
  showChrome = true;
  sysMsgVisible = false;
  private skipUnloadLogout = false;
  private headerInitPhase = true;

  private static readonly CHROMELESS_PATHS = new Set([
    '/loggedOut', '/accessDenied', '/maintenance', '/404', '/no-modules',
    '/client-landing-page',
  ]);

  constructor(
    private appService: AppService,
    private globalService: GlobalService,
    private workspaceService: WorkspaceService,
    private idleService: IdleService,
    private router: Router,
    private dialog: MatDialog,
    private formViewDrilldownRouting: FormViewDrilldownRoutingService
  ) {
    this.baseURLs = JSON.stringify({
      api: '/gtw',
      custom: '/custom',
      admin: '/admin',
      efile: '/gtw-efile-api',
    });
  }

  ngOnInit(): void {
    // Before Angular `**` → home: rewrite stage popup URLs + register `#popup` handling.
    this.formViewDrilldownRouting.installGlobalGuards();

    this.appService.startUPCompleted.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((status: any) => {
      if (status === 1) {
        this.globalService.initGlobalParams();
        this.buildChannelKey();
        this.buildHeaderData();

        setTimeout(() => { this.headerInitPhase = false; });

        if (this.appService.SERVER_CONFIG?.configSettings) {
          this.idleService.configure(this.appService.SERVER_CONFIG.configSettings);
        }
        this.idleService.watch();
        this.navigateToHomeIfRoot();
      }
    });

    this.router.events
      .pipe(filter((e): e is NavigationEnd => e instanceof NavigationEnd), takeUntilDestroyed(this.destroyRef))
      .subscribe((e: NavigationEnd) => {
        const path = e.urlAfterRedirects.split('?')[0];
        this.showChrome = !AppComponent.CHROMELESS_PATHS.has(path);
        if (!this.showChrome) {
          this.sysMsgVisible = false;
        }

        const sessionClientKey = sessionStorage.getItem('client_key');
        const urlMatch = e.urlAfterRedirects.match(/[?&]client_key=([^&]+)/);
        const urlClientKey = urlMatch ? urlMatch[1] : null;

        if (urlClientKey && urlClientKey !== sessionClientKey) {
          sessionStorage.setItem('client_key', urlClientKey);
        }

        if (sessionClientKey && !urlClientKey) {
          this.router.navigate([], {
            queryParams: { client_key: sessionClientKey },
            queryParamsHandling: 'merge',
          });
        }
      });
  }

  private buildChannelKey(): void {
    const selected = this.globalService.global?.inputs?.tax_year?.selected;
    if (selected) {
      this.channelKey = JSON.stringify({
        channel: selected.channel_key || '',
        taxYear: selected.value || '',
        scenarioKey: selected.scenario || '',
        scenarioCode: selected.scenario_type_code || '',
      });
    }
  }

  private getSelectedChannelKeyForHeader(): string | number | null {
    const key = this.globalService.global?.inputs?.tax_year?.selected?.channel_key;
    if (key === null || key === undefined || key === '') {
      return null;
    }
    return key;
  }

  private channelValueMatchesSelected(
    channelValue: string | number | null | undefined,
    selectedKey: string | number | null
  ): boolean {
    if (selectedKey === null) return false;
    if (channelValue === null || channelValue === undefined) return false;
    return String(channelValue) === String(selectedKey);
  }

  private buildChannelsFromUserNavigatorV1(selectedChannelKey: string | number | null): any[] {
    const raw = this.appService.USER_MENU_V1;
    if (!Array.isArray(raw) || raw.length === 0) {
      return [];
    }

    return raw
      .filter((item: any) => {
        if (item == null) return false;
        if (Object.prototype.hasOwnProperty.call(item, 'type') && item.type !== 'channel') {
          return false;
        }
        return true;
      })
      .map((item: any) => ({
        value: item.id ?? item.channel_key ?? item.CHANNEL_KEY ?? item.child_id ?? item.ID,
        text: item.label ?? item.text ?? item.LABEL ?? item.CHANNEL_DESC ?? '',
        selected: this.channelValueMatchesSelected(
          item.id ?? item.channel_key ?? item.CHANNEL_KEY ?? item.child_id ?? item.ID,
          selectedChannelKey
        ),
      }))
      .filter((c: any) => c.value !== null && c.value !== undefined && String(c.value).trim() !== '');
  }

  private buildChannelsFromUserMenuV2(selectedChannelKey: string | number | null): any[] {
    const userMenu = this.appService.USER_MENU || [];
    if (!Array.isArray(userMenu) || userMenu.length === 0) {
      return [];
    }
    return userMenu.map((item: any, index: number) => {
      const value = item.id ?? item.child_id ?? item.channel_key;
      const selected =
        selectedChannelKey != null
          ? this.channelValueMatchesSelected(value, selectedChannelKey)
          : index === 0;
      return { value, text: item.label ?? item.text ?? '', selected };
    });
  }

  private buildChannelDataForHeader(): any[] {
    const selectedChannelKey = this.getSelectedChannelKeyForHeader();
    let channels = this.buildChannelsFromUserNavigatorV1(selectedChannelKey);

    if (channels.length === 0) {
      channels = this.buildChannelsFromUserMenuV2(selectedChannelKey);
    }

    if (channels.length > 0 && !channels.some((c: any) => c.selected)) {
      channels = channels.map((c: any, i: number) => ({ ...c, selected: i === 0 }));
    }

    return channels;
  }

  /**
   * Application dropdown uses /getUserNavigator (USER_MENU_V1) — full channel list.
   * /v2/getUserNavigator (USER_MENU) stays for workspace / navigation tree only.
   */
  private buildHeaderData(): void {
    const channels = this.buildChannelDataForHeader();

    const headerObj = {
      globalParams: { ...this.globalService.global.globalParams },
      clientData: {
        clients: this.globalService.getClients(),
        client: this.globalService.getClient(),
      },
      channelData: channels,
      taxYearScenarioData: { ...this.globalService.global.inputs.tax_year },
      userSettingsData: { ...this.appService.USER_SETTINGS },
      serverConfigData: { ...this.appService.SERVER_CONFIG },
      generalConfigData: { ...environment },
    };

    this.headerData = JSON.stringify(headerObj);
    this.currentTheme = this.appService.USER_SETTINGS?.user?.selected_theme || 'theme-black';
  }

  private isHomeScreen(screen: any): boolean {
    return screen?.screen_key === '10906' || screen?.link === 'workspace.modules';
  }

  private getScreenRoute(screen: any): string {
    const link = String(screen?.link || screen?.SCREEN_LINK || '').trim();
    if (link) {
      return link;
    }
    return String(screen?.url || '').replace(/^\//, '').trim();
  }

  private isStandaloneScreen(screen: any): boolean {
    const route = this.getScreenRoute(screen);
    if (!route) return false;
    return !route.startsWith('workspace.');
  }

  private hasAngularRoute(path: string): boolean {
    if (!path) {
      return false;
    }
    return this.router.config.some((r: any) => r?.path === path);
  }

  private navigateWorkspaceScreen(screen: any): void {
    // Legacy parity: `GlobalControllers.js` `load-screens` handler always
    // calls `workspaceFactory.loadLink('link', '{}', activity_key, screen_key)`,
    // which internally `findActivity(activity_key)` + `findScreen(activity,
    // screen_key)` against the live USER_MENU tree.
    //
    // The migrated flow used to read `screen.parent` first. After left-nav
    // search, `screen.parent` is the *cloned + filtered* activity whose
    // `screens` array may be trimmed to only the matched screen — assigning
    // that to `workspaceFactory.screens` in `setActivity` makes only one tab
    // appear instead of every screen under that activity.
    //
    // Always resolve canonical activity/screen from USER_MENU first; fall
    // back to link search; only use `screen.parent` as a last resort and
    // only when its `screens` array is plausibly intact.
    let activity: any = null;
    let resolvedScreen: any = screen;

    if (screen?.activity_key) {
      const canonicalActivity = this.workspaceService.findActivity(screen.activity_key);
      if (canonicalActivity) {
        activity = canonicalActivity;
        if (screen.screen_key) {
          const canonicalScreen = this.workspaceService.findScreen(
            canonicalActivity,
            screen.screen_key
          );
          if (canonicalScreen) {
            resolvedScreen = canonicalScreen;
          }
        }
      }
    }

    if (!activity) {
      const link = screen?.link || screen?.SCREEN_LINK || '';
      if (link) {
        const found = this.workspaceService.findScreenByLink(link);
        if (found?.activity) {
          activity = found.activity;
          resolvedScreen = found.screen || resolvedScreen;
        }
      }
    }

    if (!activity && screen?.parent) {
      activity = screen.parent;
    }

    screen = resolvedScreen;

    if (!activity) {
      console.warn('[AppComponent] navigateWorkspaceScreen: no activity for screen', screen);
      return;
    }

    if (screen.link && !this.appService.ROUTES?.[screen.link]) {
      const prefixed = 'workspace.' + screen.link;
      if (this.appService.ROUTES?.[prefixed]) {
        screen.link = prefixed;
      }
    }

    this.workspaceService.setActivity(activity, true, screen);
    // See WorkspaceService.getScreenNavigationCommands — most screens
    // resolve to `/workspace/<slug>`, but legacy top-level uib-router
    // parents (form-view, state-form-view) live OUTSIDE `/workspace/`.
    const commands = this.workspaceService.getScreenNavigationCommands(screen);
    const clientKey = sessionStorage.getItem('client_key') || '0';
    this.router.navigate(commands, { queryParams: { client_key: clientKey } });
  }

  onLoadScreens(screen: any): void {
    if (this.isHomeScreen(screen)) {
      this.workspaceService.setTabActive.next(screen);
      this.router.navigate(['/home']);
      return;
    }
    if (this.isStandaloneScreen(screen)) {
      this.navigateStandaloneScreen(screen);
      return;
    }

    this.navigateWorkspaceScreen(screen);
  }

  onLoadCurrentScreen(event: any): void {
    if (event?.screen && this.isHomeScreen(event.screen)) {
      this.workspaceService.setTabActive.next(event.screen);
      this.router.navigate(['/home']);
      return;
    }
    if (event?.screen) {
      if (this.isStandaloneScreen(event.screen)) {
        this.navigateStandaloneScreen(event.screen);
        return;
      }

      this.navigateWorkspaceScreen(event.screen);
    }
  }

  /**
   * Navigate to a "standalone" screen (link does not start with `workspace.`).
   *
   * If the screen exists in USER_MENU, call setActivity(_changeState=false) to
   * update the workspace context and emit setTabActive so the breadcrumb updates
   * immediately — without invoking loadTab's router-navigate side effect.
   * URL navigation is always handled by onLoadModules (dot→slash conversion),
   * which preserves the original URL behaviour for every standalone screen.
   * Any navigation WorkspaceComponent might queue from the setTabActive emission
   * is cancelled by the onLoadModules navigate that follows in the same tick.
   */
  private navigateStandaloneScreen(screen: any): void {
    const link = String(screen?.link || screen?.SCREEN_LINK || '').trim();
    const found = link ? this.workspaceService.findScreenByLink(link) : null;
    if (found?.activity && found?.screen) {
      this.workspaceService.setActivity(found.activity, false, found.screen);
    }
    this.onLoadModules(this.getScreenRoute(screen));
  }

  onLoadClient(event: any): void {
    const selectedClient = event?.selected?.[0];
    if (!selectedClient) return;
    this.globalService.setClientSelection(selectedClient);

    const dialogRef = this.dialog.open(ClientConfirmActionComponent, {
      width: '400px',
      disableClose: true,
      data: { clientName: selectedClient.client_name },
    });

    let oldClientKey: string | null = null;
    dialogRef
      .afterClosed()
      .pipe(
        switchMap((confirmed: boolean) => {
          if (!confirmed) return EMPTY;
          oldClientKey = sessionStorage.getItem('client_key');
          return this.globalService.setClient(
            selectedClient.client_key,
            environment.base_url,
          );
        }), takeUntilDestroyed(this.destroyRef),
      )
      .subscribe({
        next: (res: any) => {
          if (res?.callSuccess == '1') {
            const newKey =
              res.switch_to_client_key?.toString() ||
              selectedClient.client_key.toString();
            sessionStorage.setItem('client_key', newKey);
            this.skipUnloadLogout = true;
            this.router
              .navigate([], {
                queryParams: { client_key: newKey },
                queryParamsHandling: 'merge',
              })
              .then(() => location.reload());
          } else {
            this.openClientLoadFailed(this.resolveFailedClientName(res, selectedClient));
            if (oldClientKey) {
              sessionStorage.setItem('client_key', oldClientKey);
            }
          }
        },
        error: () => {
          this.openClientLoadFailed(selectedClient.client_name);
          if (oldClientKey) {
            sessionStorage.setItem('client_key', oldClientKey);
          }
        },
      });
  }

  private openClientLoadFailed(clientName: string): void {
    this.dialog.open(ClientLoadFailedComponent, {
      width: '500px',
      disableClose: true,
      data: { clientName: clientName || '' },
    });
  }

  private resolveFailedClientName(res: any, selectedClient: any): string {
    const clients = this.globalService.getClients() || [];
    const switchKey = res?.switch_to_client_key;
    if (switchKey !== undefined && switchKey !== null && switchKey !== '') {
      const matched = clients.find((item: any) => String(item?.client_key) === String(switchKey));
      if (matched?.client_name) {
        return matched.client_name;
      }
    }
    return selectedClient?.client_name || '';
  }

  /**
   * Translates AngularJS ui-router state names (dot-separated)
   * into Angular Router paths (slash-separated) and navigates.
   */
  onLoadModules(stateOrRoute: string): void {
    if (!stateOrRoute) return;
    const path = '/' + stateOrRoute.replace(/\./g, '/');
    this.router.navigate([path]);
  }

  /** Legacy parity: GlobalControllers.js lines 273-279 */
  onSetTaxYearScenario(taxYear: any): void {
    if (!taxYear || this.headerInitPhase) return;
    const global = this.globalService.global;
    global.inputs.tax_year.selected = taxYear;
    global.globalParams.tax_year = taxYear.value;
    global.globalParams.scenario = taxYear.scenario;
  }

  /** Legacy parity: GlobalControllers.js lines 287-304 — rebuilds channelKey to trigger nav re-init */
  onSetScenario(event: any): void {
    const gp = event?.globalAppParams;
    if (!gp || this.headerInitPhase) return;

    const globalParams = this.globalService.global.globalParams;
    globalParams.scenario = gp.scenarioKey;
    globalParams.scenario_v1 = gp.scenarioKey;
    globalParams.scenarioCode = gp.scenarioCode;
    globalParams.issue_id = '';

    const selected = this.globalService.global.inputs?.tax_year?.selected;
    if (selected) {
      selected.value = globalParams.tax_year;
      selected.scenario = globalParams.scenario;
      selected.scenario_desc = gp.scenario_desc;
      selected.scenario_type_code = gp.scenarioCode;
    }

    if (event.taxYearList) {
      this.globalService.global.inputs.tax_year.values = event.taxYearList;
    }

    this.channelKey = JSON.stringify({
      channel: gp.channel,
      taxYear: gp.taxYear,
      scenarioKey: gp.scenarioKey,
      scenarioCode: gp.scenarioCode,
    });
  }

  onLoadApplication(_channel: any): void {
    this.workspaceService.setTabActive.next(null);
    this.router.navigate(['/home']);
  }

  onGlobalParamsChanged(): void {
    if (this.headerInitPhase) return;
    this.globalService.globalParamsChanged$.next();
  }

  onSetBusinesses(businesses: any): void {
    if (businesses?.values) {
      this.globalService.global.global_selected_me = businesses.values;
    }
  }

  onSyncBusinesses(businesses: any): void {
    this.globalService.global.global_selected_me = businesses?.selected || [];
    this.globalService.globalParamsChanged$.next();
  }

  onSetIssue(issueDetails: any): void {
    if (!issueDetails) return;
    const globalParams = this.globalService.global.globalParams;
    if (issueDetails.setAllParams) {
      this.globalService.global.global_issues = issueDetails.data;
      globalParams.issue_id = issueDetails.selectedIssueData?.issue_id || '';
      globalParams.issue_short_desc = issueDetails.selectedIssueData?.issue_short_desc || '';
    } else if (issueDetails.setOnlyId) {
      globalParams.issue_id = issueDetails.selectedIssueData?.issue_id || '';
      globalParams.issue_short_desc = issueDetails.selectedIssueData?.issue_short_desc || '';
    } else {
      globalParams.issue_id = '';
      globalParams.issue_short_desc = '';
    }
  }

  /** Legacy parity: GlobalControllers.js lines 308-327 — updates USER_MENU so WorkspaceService can find screens */
  onLoadLeftNav(menuData: any): void {
    if (menuData && Array.isArray(menuData) && menuData.length > 0) {
      this.appService.USER_MENU = menuData;
      // USER_MENU was previously the raw flat API response; the navigation
      // component has now built the properly-nested tree. Re-resolve the
      // breadcrumb for the current URL so it reflects the full ancestor chain.
      this.refreshBreadcrumbForCurrentUrl();
    }
    this.emitHomeBreadcrumb();
  }

  /**
   * After USER_MENU is replaced with the fully-nested tree, find the screen
   * that corresponds to the current router URL and emit setTabActive so the
   * HeaderComponent rebuilds the breadcrumb with the correct ancestry.
   * We do NOT change the workspace state — the setTabActive emission only
   * updates context (breadcrumb, global filters). WorkspaceComponent's
   * subscriber will suppress navigation because the URL already matches.
   */
  private refreshBreadcrumbForCurrentUrl(): void {
    const path = (this.router.url.split('?')[0] || '').replace(/\/+$/, '');
    if (!path || path === '/home' || path === '') return;

    // /admin and /userSettings breadcrumbs are owned by AdminShellComponent
    // (AdminBreadcrumbService + route.data.breadcrumb).
    if (/^\/(admin|userSettings)(\/|$)/.test(path)) {
      return;
    }

    const workspaceMatch = path.match(/^\/workspace\/(.+)$/);
    if (!workspaceMatch) return;

    const slug = workspaceMatch[1];
    let found: { activity: any; screen: any } | null = null;

    if (/^\d+$/.test(slug)) {
      // Numeric screen_key URL (e.g. /workspace/10100)
      found = this.workspaceService.findScreenByKey(slug);
    } else {
      // Slug-based URL (e.g. /workspace/foot-notes)
      found = this.workspaceService.findScreenByLink('workspace.' + slug)
        || this.workspaceService.findScreenByLink(slug);
    }

    if (found?.screen) {
      this.workspaceService.setTabActive.next(found.screen);
    }
  }

  private emitHomeBreadcrumb(): void {
    // Only seed the "Modules" breadcrumb when we're actually on the home
    // route. On any other deep URL (e.g. `/workspace/expense-trans`,
    // `/section-926-report`) the destination component already owns the
    // active-screen / breadcrumb context, and emitting `workspace.modules`
    // here would clobber it — and, because `WorkspaceComponent` subscribes
    // to `setTabActive` and force-navigates to the slug, it would also
    // bounce the user from their current page to `/workspace/modules`
    // every time the left-nav menu finishes loading on refresh.
    const path = (this.router.url.split('?')[0] || '').replace(/\/+$/, '');
    if (path && path !== '/home' && path !== '' && path !== '/') {
      return;
    }
    this.navigateToHomeIfRoot();
    const found = this.workspaceService.findScreenByLink('workspace.modules');
    if (found?.screen) {
      this.workspaceService.setTabActive.next(found.screen);
    }
  }

  /**
   * Initial hash URLs (`/#/?client_key=0`) often stay at `/` until the user
   * clicks Home in the left nav. That click used to be the only path that
   * called `router.navigate(['/home'])`, leaving the main outlet blank.
   */
  private navigateToHomeIfRoot(): void {
    const path = (this.router.url.split('?')[0] || '').replace(/\/+$/, '');
    if (path && path !== '' && path !== '/') {
      return;
    }
    const clientKey = sessionStorage.getItem('client_key') || '0';
    void this.router.navigate(['/home'], { queryParams: { client_key: clientKey } });
  }

  onSysMsgVisibilityChange(visible: boolean): void {
    this.sysMsgVisible = visible;
  }

  captureChangeTheme(data: any): void {
    const theme = data === 'white' ? 'theme-white' : 'theme-black';
    this.appService.applyTheme(theme);
    this.currentTheme = theme;
  }

  /**
   * Mirrors legacy $window.onunload: fires logout API call when the tab/window is closed.
   * Uses navigator.sendBeacon (non-blocking) since synchronous XHR on unload is deprecated.
   */
  @HostListener('window:beforeunload')
  onBeforeUnload(): void {
    if (this.skipUnloadLogout) {
      return;
    }
    if (this.isLocalHost()) {
      return;
    }
    const logoutUrl = environment.base_url + '/logoutUser';
    if (navigator.sendBeacon) {
      navigator.sendBeacon(logoutUrl);
    }
  }

  /**
   * Explicit logout triggered by the header's logout-user event.
   * Stops idle watching, clears session, and navigates to /loggedOut.
   */
  onLogout(): void {
    this.skipUnloadLogout = true;
    this.showChrome = false;
    this.idleService.stop();
    sessionStorage.removeItem('Authorization');
    sessionStorage.removeItem('client_key');
    this.router.navigate(['/loggedOut']);
  }

  ngOnDestroy(): void {
    this.idleService.stop();
  }

  private isLocalHost(): boolean {
    return window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
  }
}
